import React, { ReactNode, useContext } from 'react';
import { Link } from 'react-router';
import { ThemeProvider } from 'emotion-theming';
import { genThemedStyled, Theme, ThemeContext } from '../../utilities/theme';
import { ColorCodes, colorsForTheme } from '../../foundations/color';
import { FUTURO_TYPEFACES as F } from '../../foundations/font';

export type NavigationElementProps = {
  children: ReactNode;
  variant: 'title' | 'label'; // label by default
  url: string;
  disabled?: boolean;
  target?: string;
  title?: string;
  onClick?: () => void;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  const colorThemes = {
    titleColor: colors.SPACE,
    textColor: colors.BUTTON_ORANGE,
    disabledColor: colors.STEEL,
  };
  return colorThemes;
};

type themeStyles = {
  titleColor: ColorCodes;
  textColor: ColorCodes;
  disabledColor: ColorCodes;
};

const ThemeStyled = genThemedStyled<themeStyles>();

const TextContainer = ThemeStyled.label<{
  variant: 'title' | 'label';
  theme: themeStyles;
}>(({ variant, theme }) => ({
  color: variant === 'title' ? theme.titleColor : theme.textColor,
  ':hover': {
    textDecoration: 'underline',
  },
  ...(variant === 'title' ? F.H4 : F.LABEL),
}));

const DisabledTextContainer = ThemeStyled.label<{
  variant: 'title' | 'label';
  theme: themeStyles;
}>(({ variant, theme }) => ({
  color: theme.disabledColor,
  pointerEvents: 'none',
  ...(variant === 'title' ? F.H4 : F.LABEL),
}));

export const AnchorNavigation = (props: NavigationElementProps) => {
  const {
    children,
    variant = 'label',
    url,
    disabled,
    target,
    title,
    onClick,
  } = props;
  return (
    <ThemeProvider theme={getTheme(useContext(ThemeContext))}>
      {disabled ? (
        <b>
          <DisabledTextContainer variant={variant}>
            {children}
          </DisabledTextContainer>
        </b>
      ) : (
        <a href={url} title={title} target={target} onClick={onClick}>
          <TextContainer variant={variant}>{children}</TextContainer>
        </a>
      )}
    </ThemeProvider>
  );
};

export const LinkNavigation = (props: NavigationElementProps) => {
  const { children, url, disabled, target, title, variant = 'label' } = props;
  return (
    <ThemeProvider theme={getTheme(useContext(ThemeContext))}>
      {disabled ? (
        <b>
          <DisabledTextContainer variant={variant}>
            {children}
          </DisabledTextContainer>
        </b>
      ) : (
        <Link to={url} title={title} target={target}>
          <TextContainer variant={variant}>{children}</TextContainer>
        </Link>
      )}
    </ThemeProvider>
  );
};
